import { useSuiAuth } from "@/components/providers/SuiAuthProvider";
import { suiExplorer } from "@/lib/sui";
import { SUI_DEPLOY } from "@/lib/suiConfig";

/**
 * Settings (Sui). Operator identity, network, and the deployment coordinates
 * the dashboard talks to. Minimal by intent — the policy lives on-chain, not in
 * app settings. DESIGN.md Persian + Orange: ghost ground, mono data, hairlines.
 */

function short(id: string, head = 10, tail = 8): string {
  return id.length <= head + tail + 1 ? id : `${id.slice(0, head)}…${id.slice(-tail)}`;
}

export function SuiSettingsPage() {
  const { suiAddress, signOut } = useSuiAuth();

  const deploy: { label: string; value: string; href?: string }[] = [
    { label: "Core package", value: SUI_DEPLOY.corePkg, href: suiExplorer.object(SUI_DEPLOY.corePkg) },
    { label: "DeepBook adapter", value: SUI_DEPLOY.adapterPkg, href: suiExplorer.object(SUI_DEPLOY.adapterPkg) },
    { label: "Adapter registry", value: SUI_DEPLOY.registry, href: suiExplorer.object(SUI_DEPLOY.registry) },
    { label: "DeepBook pool", value: SUI_DEPLOY.pool, href: suiExplorer.object(SUI_DEPLOY.pool) },
    { label: "Quote asset", value: SUI_DEPLOY.quoteType },
    { label: "Base asset", value: SUI_DEPLOY.baseType },
  ];

  return (
    <div className="st">
      <header className="st__head">
        <div className="st__kick">sui · operator</div>
        <h1 className="st__title">Settings</h1>
      </header>

      <section className="st__panel">
        <div className="st__ph"><h2>Identity</h2></div>
        <div className="st__row">
          <span className="st__lbl">Operator wallet</span>
          {suiAddress ? (
            <a className="st__val" href={suiExplorer.address(suiAddress)} target="_blank" rel="noopener noreferrer">{short(suiAddress)} ↗</a>
          ) : <span className="st__val">—</span>}
        </div>
        <div className="st__row">
          <span className="st__lbl">Network</span>
          <span className="st__val"><span className="st__pill">sui testnet</span></span>
        </div>
        <div className="st__row">
          <span className="st__lbl">Session</span>
          <button type="button" className="st__signout" onClick={signOut}>Sign out</button>
        </div>
      </section>

      <section className="st__panel">
        <div className="st__ph"><h2>Deployment</h2><span className="st__ph-note">the on-chain packages this dashboard enforces against</span></div>
        {deploy.map((d) => (
          <div className="st__row" key={d.label}>
            <span className="st__lbl">{d.label}</span>
            {d.href ? (
              <a className="st__val" href={d.href} target="_blank" rel="noopener noreferrer">{short(d.value)} ↗</a>
            ) : <span className="st__val">{short(d.value)}</span>}
          </div>
        ))}
      </section>

      <style>{`
        .st { max-width: 760px; margin: 0 auto; font-family: var(--font-body); color: var(--bone); }
        .st__head { margin-bottom: var(--s-5); }
        .st__kick { font: var(--type-kicker); letter-spacing: var(--tracking-kicker); text-transform: uppercase; color: var(--accent-deep); }
        .st__title { font: var(--type-display-page); font-style: italic; margin: var(--s-2) 0 0; }
        .st__panel { background: var(--bg-elev); border: 1px solid var(--line); border-radius: var(--r-md); padding: var(--s-5) var(--s-6); margin-bottom: var(--s-5); }
        .st__ph { display: flex; align-items: baseline; gap: var(--s-4); margin-bottom: var(--s-4); }
        .st__ph h2 { font: var(--type-display-section); font-style: italic; }
        .st__ph-note { font: var(--type-body-small); color: var(--slate-2); }
        .st__row { display: flex; align-items: center; justify-content: space-between; gap: var(--s-5); padding: 12px 0; border-top: 1px solid var(--line-soft); }
        .st__row:first-of-type { border-top: 0; }
        .st__lbl { font: var(--type-kicker); letter-spacing: var(--tracking-kicker); text-transform: uppercase; color: var(--slate-3); }
        .st__val { font: var(--type-mono-data); color: var(--bone); }
        a.st__val:hover { color: var(--accent-deep); }
        .st__pill { font: var(--type-mono-address); text-transform: uppercase; letter-spacing: 0.08em; color: var(--accent-deep); border: 1px solid var(--accent); border-radius: 999px; padding: 3px 10px; }
        .st__signout { font: var(--type-label); letter-spacing: 0.04em; text-transform: uppercase; color: var(--oxblood); border: 1px solid var(--oxblood); border-radius: var(--r-sm); padding: 8px 16px; transition: background 150ms ease; }
        .st__signout:hover { background: var(--oxblood-tint); }
      `}</style>
    </div>
  );
}
